"use client";

import { Dialog } from "@base-ui/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import ExtendedImage from "@/components/ui/extended-image";

interface ImageLightboxProps {
  images: string[];
  startIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ImageLightbox = ({ images, startIndex = 0, open, onOpenChange }: ImageLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    if (open) setCurrentIndex(startIndex);
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setCurrentIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setCurrentIndex((i) => Math.min(images.length - 1, i + 1));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, images.length]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 bg-black/90 transition-all duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0" />
        <Dialog.Popup className="fixed inset-0 flex items-center justify-center outline-none transition-all duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0">
          <Dialog.Close
            className="absolute top-4 right-4 z-10 flex items-center justify-center size-10 rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
            render={<button type="button" />}
          >
            <X className="size-5" />
          </Dialog.Close>

          {images.length > 1 && (
            <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium tabular-nums select-none">
              {currentIndex + 1} / {images.length}
            </span>
          )}

          {hasPrev && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center size-10 rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
              onClick={() => setCurrentIndex((i) => i - 1)}
              type="button"
            >
              <ChevronLeft className="size-6" />
            </button>
          )}

          <div className="relative w-[90vw] h-[90vh]">
            <ExtendedImage
              alt={`Resim ${currentIndex + 1}`}
              className="object-contain"
              fill
              sizes="90vw"
              src={images[currentIndex]}
            />
          </div>

          {hasNext && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center size-10 rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
              onClick={() => setCurrentIndex((i) => i + 1)}
              type="button"
            >
              <ChevronRight className="size-6" />
            </button>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ImageLightbox;
